import { useLocation, useNavigate } from 'react-router-dom';

import ArtworkDisplay from './add-element/add-board-artwork/ArtworkDisplay';
import ImageDisplay from './add-element/add-picture/ImageDisplay';
import PollDisplay from './add-element/add-polls/PollDisplay';
import QuizDisplay from './add-element/add-quiz/QuizDisplay';
import VideoDisplay from './add-element/add-video/VideoDisplay';

import './add-element/styles/EmbeddedContent.css';
import './styles/Mentions.css';

/**
 * Props:
 *   structure     = array of { type, id } blocks
 *   textsById     = { [textId]: { content } }
 *   picturesById  = { [pictureId]: Array<pic> }
 *   artworksById  = { [artworkId]: artworkObj }
 *   pollsById     = { [pollId]: pollObj }    // options = [{text,votes},…]
 *   quizzesById   = { [quizId]: quizObj }    // options = [text,…]
 *   onVote        = fn(pollId, optionIndex)  // must return updated options
 *   onAnswer      = fn(quizId, optionIndex)  // must return updated options
 *   previewMode   = boolean                  // true for published view
 *   interactive   = boolean                  // if false, hide edit/remove UI
 */
export default function BoardContentRenderer({
  structure,
  postId,
  textsById,
  picturesById,
  artworksById,
  videosById,
  pollsById,
  quizzesById,
  onVote,
  onAnswer,
  previewMode,
  interactive = true,
  postMode = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to process tag spans in each text block
  // TODO: Implement Tags
  const processTextHtml = (html) => {
    // Replace tag spans with <a> for clickable usernames
    const replaced = html.replace(
      /<span class="tag-display"[^>]*>(@([\w]+))<\/span>/g,
      '<a href="/user/$2" class="tag-display">$1</a>'
    );
    // Split on <a> tags
    return replaced.split(/(<a class="tag-display"[^>]*>@[\w]+<\/a>)/g);
  };

  const mediaElements = ["post_artworks", "post_pictures", "post_videos"];
  // In post we show first the media element
  const orderStructure = () => {
    const media = structure.filter(block => mediaElements.includes(block.type));
    const nonMedia = structure.filter(block => !mediaElements.includes(block.type));
    return [...media, ...nonMedia];
  };

  const orderedStructure = postMode ? orderStructure() : structure;

  return (
    <div className="board-content-renderer">
      {orderedStructure.map(block => {
        const blockKey = `${block.type}:${block.id}`;

        switch (block.type) {
        case 'post_texts': {
          const txt = textsById[block.id];
          if (!txt) return null;
          // ——— Edit mode: one block per paragraph, as before ———
          if (!previewMode) {
            return (
              <div
                key={blockKey}
                dangerouslySetInnerHTML={{ __html: txt.content }}
              />
            );
          }

          // Published: process each text block individually
          const parts = processTextHtml(txt.content);

          return (
            <div key={blockKey} className="post__text-block">
              {parts.map((chunk, i) => {
                const m = chunk.match(
                  /<a class="tag-display"[^>]*>@([\w]+)<\/a>/
                );
                if (m) {
                  const username = m[1];
                  return (
                    <span key={i}>
                      <button
                        className="tag-display"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(
                            `/user/${username}`,
                            {
                              state: {
                                from: location.pathname,
                                scrollY: window.scrollY
                              }
                            }
                          );
                        }}
                      >
                        @{username}
                      </button>
                    </span>
                  );
                }
                // Everything else: render raw HTML chunk
                return <span key={i} dangerouslySetInnerHTML={{ __html: chunk }} />;
              })}
            </div>
          );
        }

        case 'post_pictures': {
          const groupImages = picturesById[block.id] || {};
          if(Object.keys(groupImages).length === 0 || groupImages?.pictures?.length === 0) 
            return null;

          return (
            <ImageDisplay 
              key={blockKey}
              groupId={groupImages.groupId}
              pictures={groupImages.pictures}
              previewMode={previewMode}
              handleImageRemove={null}
              handleCaptionChange={null}
            />
          );
        }

        case 'post_artworks': {
          const groupedArtworks = artworksById[block.id] || [];
          if(Object.keys(groupedArtworks).length === 0 || groupedArtworks?.artworks?.length === 0) 
            return null; 

          return (
            <ArtworkDisplay
              key={blockKey}
              groupedArtworks={groupedArtworks}
              onArtworkRemove={null}
              previewMode={previewMode}
              navigate={navigate}
              location={location}
            />
          );

        }

        case 'post_videos': {
          const video = videosById?.[block.id];
          if (!video) return null;

          return (
            <VideoDisplay
              key={blockKey}
              groupId={video.groupId}
              postId={postId}
              video={video}
              previewMode={previewMode}
            />
          );
        }

        case 'post_polls': {
          const poll = pollsById[block.id];
          if (!poll) return null;
          return (
            <div
              key={blockKey}
              className={`post__poll-block${poll.hasVoted ? ' answered' : ''}`}
              onClick={e => e.stopPropagation()}
            >
              <PollDisplay
                pollId={poll.pollId}
                title={poll.question}
                options={poll.options}
                initialHasVoted={poll.hasVoted}
                initialUserAnswer={poll.userAnswer}
                onVote={previewMode ? onVote : undefined}
                previewMode={!interactive}
              />
            </div>
          );
        }

        case 'post_quizzes': {
          const quiz = quizzesById[block.id];
          if (!quiz) return null;
          return (
            <div
              key={blockKey}
              className={`post__quiz-block${quiz.hasAnswered ? ' answered' : ''}`}
              onClick={e => e.stopPropagation()}
            >
              <QuizDisplay
                quizId={quiz.quizId}
                question={quiz.question}
                options={quiz.options.map(o => typeof o === 'string' ? o : o.text)}
                correctOption={quiz.correctOption}
                initialHasAnswered={quiz.hasAnswered}
                userAnswer={quiz.userAnswer}
                onAnswer={previewMode ? onAnswer : undefined}
                previewMode={!interactive || quiz.hasAnswered}
                disableSelection={!interactive || quiz.hasAnswered}
              />
            </div>
          );
        }

        default:
          return null;
        }
      })}
    </div>
  );
}
